import { appendQueryStringParams } from 'local/url'

import { SessionMonitor, EventHandler } from './types'
import { createEventHandlers, emptyMethod } from './utils'

export default (ulsUrl: string): SessionMonitor => {
	const eventHandlers = {
		sessiontimeout: createEventHandlers(),
		closebrowser: createEventHandlers(),
		redirecttologin: createEventHandlers(),
		userreauthenticated: createEventHandlers(),
	}

	// Support adamuiserver development
	if (!ulsUrl) {
		return {
			addEventListener: emptyMethod,
			updateExpiration: emptyMethod,
			keepUserSessionActive: emptyMethod,
			expireUserSession: emptyMethod,
			renderTimeOutPage: emptyMethod,
			createSignoutRequestWithoutRedirect: emptyMethod,
		}
	}

	// TODO: This needs to be in the configuration?
	// For now default value for this is 'sessiontimeout'
	const sessionTimeoutDisplay = 'sessiontimeout'

	let tokenExpiryTimeout: ReturnType<typeof setTimeout>
	const handleExpirationChange = (willExpireIn: number, authHeader: string, sessionId: string) => {
		if (tokenExpiryTimeout) {
			clearTimeout(tokenExpiryTimeout)
			tokenExpiryTimeout = undefined
		}
		tokenExpiryTimeout = setTimeout(
			async () => {
				const isExistingSession = await checkExistingSession(authHeader, sessionId)
				if (!isExistingSession) {
					updateActivityMonitorSession(sessionId, authHeader)
				} else {
					const isActive = await checkActivityMonitorSession(authHeader)
					if (!isActive) {
						renderSessionTimeoutDisplay(sessionTimeoutDisplay)
					}
				}
			},
			Math.max(0, willExpireIn),
		)
	}

	const renderSessionTimeoutDisplay = (mode: 'sessiontimeout' | 'redirecttologin' | 'closebrowser') => {
		// There are 3 modes of login display if the session expired
		// 1. [sessiontimeout] shows session expired screen
		// 2. [redirecttologin] redirect the user to the login screen
		// 3. [closebrowser] close the browser
		switch (mode) {
			case 'sessiontimeout':
				eventHandlers.sessiontimeout.emit(undefined)
				break
			case 'redirecttologin':
				eventHandlers.redirecttologin.emit(undefined)
				break
			case 'closebrowser':
				eventHandlers.closebrowser.emit(undefined)
				break
		}
	}

	const checkActivityMonitorSession = async (authHeader: string) => {
		const isActive = await fetch(`${ulsUrl}/activity-monitor/is-active`, {
			headers: {
				Authorization: `Bearer ${authHeader}`,
			},
		})
			.then(async (response) => {
				if (response.ok) {
					const result = await response.json()
					if (!result) {
						return false
					}

					return true
				}

				return false
			})
			.catch(() => {
				return false
			})

		return isActive
	}

	const checkExistingSession = async (authHeader: string, sessionId: string) => {
		const sessionExists = await fetch(
			appendQueryStringParams(`${ulsUrl}/activity-monitor/is-session-exists`, { sid: sessionId }),
			{
				headers: {
					Authorization: `Bearer ${authHeader}`,
				},
			},
		)
			.then(async (response) => {
				if (response.ok) {
					const result = await response.json()
					if (!result) {
						return false
					}
					return true
				}
				return false
			})
			.catch(() => {
				return false
			})

		return sessionExists
	}

	const updateActivityMonitorSession = async (sessionId: string, authHeader: string) => {
		await fetch(`${ulsUrl}/activity-monitor/update`, {
			headers: {
				Authorization: `Bearer ${authHeader}`,
				'Content-Type': 'application/json',
			},
			method: 'POST',
			body: JSON.stringify({ sessionId }),
		})
			.then(async (response) => {
				if (response.status === 200) {
					const result = await response.text()
					//update route return result if the session is still active or not.
					switch (result) {
						case 'timeout':
							renderSessionTimeoutDisplay(sessionTimeoutDisplay)
							break
						case 'logout':
							renderSessionTimeoutDisplay(sessionTimeoutDisplay)
							break
						case 'active':
							break
					}
				}
			})
			.catch((error) => {
				error.source = 'createsessionmonitor'
				throw error
			})
	}

	const expireActivityMonitorSession = (sessionId: string, authHeader: string) => {
		fetch(`${ulsUrl}/activity-monitor/clear-session`, {
			headers: {
				Authorization: `Bearer ${authHeader}`,
				'Content-Type': 'application/json',
			},
			method: 'POST',
			body: JSON.stringify({ sessionId }),
		})
			.then((response) => {
				if (!response.ok) {
					const error = new Error('Failed to clear the user activity monitor session.') as any
					error.status = response.status
					throw error
				}
			})
			.catch((error) => {
				error.source = 'createsessionmonitor'
				throw error
			})
	}

	const signoutRequestWithoutRedirect = (idTokenHint: string) => {
		const iFrameSignoutRequest = 'iFrameSignoutRequest'
		const existingIframe = document.getElementById(iFrameSignoutRequest)

		// Add an iframe with the purpose of when the user session has expired will call the endsession endpoint to logout the user.
		if (!existingIframe) {
			const iframe = document.createElement('iframe')

			iframe.style.cssText = 'visibility: hidden; position: absolute; display: hidden; width: 0px; height: 0px;'
			iframe.src = `${ulsUrl}/connect/endsession?id_token_hint=${idTokenHint}`
			iframe.id = iFrameSignoutRequest

			document.documentElement.appendChild(iframe)

			iframe.addEventListener('load', () => {
				// Removed the iframe after it has loaded.
				setTimeout(() => {
					const createdIframe = document.getElementById(iFrameSignoutRequest)
					if (createdIframe) {
						createdIframe.remove()
					}
				}, 1000)
			})
		}
	}

	return {
		addEventListener: (type, handler: EventHandler) => {
			eventHandlers[type].addListener(handler)
			return () => eventHandlers[type].removeListener(handler)
		},
		updateExpiration: handleExpirationChange,
		keepUserSessionActive: updateActivityMonitorSession,
		expireUserSession: expireActivityMonitorSession,
		renderTimeOutPage: renderSessionTimeoutDisplay,
		createSignoutRequestWithoutRedirect: signoutRequestWithoutRedirect,
	}
}
